import { useContext } from 'react'
import { FormikContext } from 'formik'
import { showContentModal } from '~/lib/modal'
import { motion, AnimatePresence } from "framer-motion"
import classnames from 'classnames/bind'
import { StoreContext } from '~/store'
import { StepperContext } from '~/components/ui/ViewStepper'
import { getPackages, formatReadableDate } from '~/utils/format'
import { formatAddressParts } from '~/utils/format'
import { getTaxiAndEta, getPackageText, getPriceFromEstimate, hasTaxiAndEta } from '~/utils/data'
import { ChangeButton, SummaryIcon, CharIcon, BookingSummaryField, EditField } from '~/components'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const cx = classnames.bind(styles)

const formatAddress = address => {
  return address.streetNumber 
    ? address.streetName + ' ' + address.streetNumber
    : address.streetName
}

const renderPaymentIcon = (values) => {
  if (values.payment === 'PayInCar') {    
    return <SummaryIcon id="PayInCar"></SummaryIcon>
  }
  else {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const icon = isCreditCard ? `Card${values.account.cardType}` : 'TSAB';
    return <SummaryIcon id={icon}></SummaryIcon>
  }
}

const renderPayment = (values) => {
  if (values.payment === 'DefaultNoSelect') {    
    return (
      <div className={styles.payment}>
         <div className={cx(common.subtitle, styles.paymentType)}></div>
      </div>
    );
  }
  else if (values.payment === 'PayInCar') {    
    return (
      <div className={styles.payment}>
         <div className={cx(common.subtitle, styles.paymentType)}>{i18next.t('title-pay-in-car')}</div>
      </div>
    );
  }
  else {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const title = isCreditCard ? values.account.maskedAccount : values.account.accountName
    const subtitle = isCreditCard ? values.account.cardType : values.account.accountNumber
    const icon = isCreditCard ? `Card${values.account.cardType}` : 'TSAB';
    return (
      <div className={styles.payment}>
          <div className={cx(common.subtitle, styles.paymentType)}>{title}</div>
          <div className={common.subtitle}>&nbsp;({subtitle})</div>
      </div>
    )
  }
}

const renderPrice = (values) => {
  if(!values?.priceEstimate?.prices) return null;
  console.log('price', values?.priceEstimate?.prices, values?.serviceType)
  const price = getPriceFromEstimate(values?.priceEstimate?.prices, values?.serviceType)
  if (!price) return null
  return `${price}:-`
}

export default function PackageBookingSummary({ values, showHow = false, showPayment = false }) {
  const { t } = useTranslation()
  const { user, bookings } = useContext(StoreContext)
  const { prev } = useContext(StepperContext)
  const { setFieldValue } = useContext(FormikContext) || {}

  const booking = bookings?.find(item => item.id === values.bookingId)

  const hasDeparture = values.departure?.address !== undefined
  const hasDestination = values.destination?.address !== undefined
  const fromAddressParts = hasDeparture ? formatAddressParts(values.departure) : null
  const toAddressParts = hasDestination ? formatAddressParts(values.destination) : null

  const taxiAndEta = booking ? getTaxiAndEta(booking) : ''
  const time = values.pickupTime ? formatReadableDate(values.pickupTime) : t('label-as-soon-as-possible')
  const showPrice = values.priceEstimate
  // Label by the intended price type: with a failed estimate we are still on fixed price, not taximeter
  const isFixedPrice = (values.autoValues?.fixedPrice ?? values.fixedPrice) !== false
  const packages = getPackages(values)
  const hasBooking = !isNaN(values?.bookingId)

  const openEditMessage = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={(id, val) => setFieldValue?.(id, val, false)} fields={[{id:'packageDeliveryInfo.otherInformation', title:t('title-extra-information')}]} title={t('title-extra-information')} onClose={close} />
  ))

  return (
    <>
      <MotionElement.div className={common.content}>
        {hasBooking && <div className={styles.group}>  
          <div className={styles.groupContent}>
            
            <div className={styles.groupContentColumn}>
              <div className={styles.groupContentMainColumn}>
                <div className={styles.label}>{t('label-booking-number')}</div>  
              </div>
              <div className={common.title}>{values.bookingId}</div>
            </div>
          </div>
        </div>}

        <div className={common.group}>
          <div className={styles.group}>
            <div className={styles.groupIcon}>
              <SummaryIcon id="Prebooked"></SummaryIcon>
            </div>
            <div className={styles.groupContent}>
              <div className={styles.groupContentColumn}>
                <div className={styles.groupContentMainColumn}>
                  <div className={cx(common.title, common.capitalize)}>{time}</div>
                  {values.checkedInBaggage && <ul className={styles.options}>
                  <AnimatePresence>
                    <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>Incheckat bagage</motion.li>
                    </AnimatePresence>
                  </ul> }
                  {hasTaxiAndEta(booking) && taxiAndEta && <div className={common.subtitle}>{taxiAndEta}&nbsp;</div>}
                </div>
                {!hasBooking && <ChangeButton onClick={() => prev('time')}/>}
              </div>
            </div>
          </div>
        </div>

        <div className={cx(common.group, { showStops: true} )}>
          <div>
            <div className={cx(styles.content, styles.departure, { hasStops:true })}>
              <CharIcon char="A" className={styles.charIcon} small />
              <div className={common.title}>{fromAddressParts?.title}</div>
              <div className={common.subtitle}>{fromAddressParts?.subtitle}</div>
            </div>
          </div>
          {hasDestination && <div>
            <div className={cx(styles.content, styles.destination, { hasStops:true })}>
              {<CharIcon char="B" className={styles.charIcon} small />}
              <div className={common.title}>{toAddressParts?.title}</div>
              <div className={common.subtitle}>{toAddressParts?.subtitle}</div>
            </div>
          </div>}
        </div>

        <div className={common.group}>
          <div className={common.group}>
            <div className={styles.group}>
              <div className={styles.groupIcon}>
                {renderPaymentIcon(values)}
              </div>
              <div className={styles.groupContent}>
                <div className={styles.groupContentColumn}>
                  <div className={styles.groupContentMainColumn}>
                    <div className={cx(common.title)}>{isFixedPrice ? t('label-fixed-price') : 'Taxameter'}</div>
                    { showPayment && <>
                      {renderPayment(values)}
                    </> }
                    {<ul className={styles.options}>
            <AnimatePresence>
              {packages.map((value, index) => <motion.li key={index} transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>{getPackageText(value)}</motion.li>)}
              </AnimatePresence>
            </ul>}
                  </div>
                  <div className={styles.price}>{renderPrice(values)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={common.group}>
          <div className={styles.group}>
            <div className={styles.groupIcon}>
              <SummaryIcon id="Sender"></SummaryIcon>
            </div>
            <div className={styles.groupContent}>
              <div className={styles.groupContentColumn}>
                <div className={styles.groupContentMainColumn}>
                  <div className={common.title}>{values.name}</div>
                  <div className={common.subtitle}>{values.phone}</div>
                  { (values.packageDeliveryInfo.packageSender.nameOnDoor || 
                     values.packageDeliveryInfo.packageSender.numberOfStairs || 
                     values.packageDeliveryInfo.packageSender.doorCode) && 
                    <ul className={styles.options}>
                      <AnimatePresence>
                        {values.packageDeliveryInfo.packageSender.nameOnDoor && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}><SummaryIcon id="NameOnDoor" small></SummaryIcon>{values.packageDeliveryInfo.packageSender.nameOnDoor}</motion.li> }
                        {values.packageDeliveryInfo.packageSender.numberOfStairs && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}><SummaryIcon id="Stairs" small></SummaryIcon>{values.packageDeliveryInfo.packageSender.numberOfStairs}</motion.li> }
                        {values.packageDeliveryInfo.packageSender.doorCode && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}><SummaryIcon id="Keypad" small></SummaryIcon>{values.packageDeliveryInfo.packageSender.doorCode}</motion.li> }
                        </AnimatePresence>
                      </ul> 
                    }
                </div>
                <ChangeButton onClick={() => prev('sender')}/>
              </div>
            </div>
          </div>
        </div>

        {values.packageDeliveryInfo.packageReceiver.nameOnDoor && 
          <div className={common.group}>
            <div className={styles.group}>
              <div className={styles.groupIcon}>
                <SummaryIcon id="Receiver"></SummaryIcon>
              </div>
              <div className={styles.groupContent}>
                <div className={styles.groupContentColumn}>
                  <div className={styles.groupContentMainColumn}>
                    <div className={common.title}>{values.packageDeliveryInfo.packageReceiver.nameOnDoor}</div>
                    <div className={common.subtitle}>{values.packageDeliveryInfo.packageReceiver.phoneNumber}</div>
                    { (values.packageDeliveryInfo.packageReceiver.numberOfStairs || 
                      values.packageDeliveryInfo.packageReceiver.doorCode ||
                      values.packageDeliveryInfo.packageReceiver.leaveOutsideDoor ) && 
                      <ul className={styles.options}>
                        <AnimatePresence>
                          {values.packageDeliveryInfo.packageReceiver.numberOfStairs && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}><SummaryIcon id="Stairs" small></SummaryIcon>{values.packageDeliveryInfo.packageReceiver.numberOfStairs}</motion.li> }
                          {values.packageDeliveryInfo.packageReceiver.doorCode && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}><SummaryIcon id="Keypad" small></SummaryIcon>{values.packageDeliveryInfo.packageReceiver.doorCode}</motion.li> }
                          {values.packageDeliveryInfo.packageReceiver.leaveOutsideDoor && <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>{t('title-leave-outside')}</motion.li> }
                        </AnimatePresence>
                      </ul> 
                    }
                  </div>
                  <ChangeButton onClick={() => prev('receiver')}/>
                </div>
              </div>
            </div>
          </div>
        }

        <div className={common.group}>
          <BookingSummaryField title={t('title-extra-information')} value={values.packageDeliveryInfo?.otherInformation ? values.packageDeliveryInfo.otherInformation : '-'} icon="Message" changeToggle={!hasBooking ? openEditMessage : null}></BookingSummaryField>
        </div>
      </MotionElement.div>
    </>
  )
}
